"use client"
import React, { useState, useEffect, useRef } from "react";
import {
  Dropdown,
  OverlayTrigger,
  Tooltip,
  Modal,
  Button
} from "react-bootstrap";
import { useAppData } from "@/lib/providers/AppDataProvider";
import "@/components/locator/LocatorSearchFilters.css";
import { useMediaQuery } from "react-responsive";

// Reusable component for number range dropdowns
const RangeDropdown = ({
  label,
  minValue,
  maxValue,
  onMinChange,
  onMaxChange,
  onClear,
  minPlaceholder,
  maxPlaceholder,
  prefix = "",
  width = "8rem"
}) => {
  const [show, setShow] = useState(false);
  const dropdownRef = useRef(null);

  const hasValue = minValue || maxValue;

  const handleClear = e => {
    e.stopPropagation();
    onClear();
  };

  const formatDisplayValue = () => {
    if (minValue && maxValue) {
      return `${prefix}${minValue}-${prefix}${maxValue}`;
    } else if (minValue) {
      return `${prefix}${minValue}+`;
    } else if (maxValue) {
      return `Up to ${prefix}${maxValue}`;
    }
    return label;
  };

  return (
    <Dropdown
      show={show}
      onToggle={isOpen => setShow(isOpen)}
      ref={dropdownRef}
      style={{ width: width }}
    >
      <Dropdown.Toggle
        variant="secondary"
        className="btn btn-sm btn-secondary"
        style={{
          width: width,
          marginRight: "0.5rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap"
        }}
      >
        {formatDisplayValue()}
        <div style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
          <div style={{ display: hasValue ? "block" : "none" }}>
            <i
              className="fa-solid fa-times"
              onClick={handleClear}
              style={{
                fontSize: "0.875rem",
                cursor: "pointer",
                padding: "0 0.25rem"
              }}
            />
          </div>
        </div>
      </Dropdown.Toggle>
      <Dropdown.Menu
        renderOnMount={true}
        popperConfig={{ strategy: "fixed" }}
        onClick={e => e.stopPropagation()}
        style={{ padding: "1rem", minWidth: "280px" }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <input
            type="number"
            className="form-control form-control-sm"
            placeholder={minPlaceholder}
            value={minValue || ""}
            onChange={e =>
              onMinChange(e.target.value ? parseInt(e.target.value) : null)
            }
            onClick={e => e.stopPropagation()}
            style={{ flex: 1 }}
          />
          <span style={{ color: "#6c757d" }}>to</span>
          <input
            type="number"
            className="form-control form-control-sm"
            placeholder={maxPlaceholder}
            value={maxValue || ""}
            onChange={e =>
              onMaxChange(e.target.value ? parseInt(e.target.value) : null)
            }
            onClick={e => e.stopPropagation()}
            style={{ flex: 1 }}
          />
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
};

// Reusable component for amenity multi-select with modal
const AmenityDropdown = ({
  label = "Features",
  amenityOptions = [],
  selectedAmenities = [],
  onSelectionChange,
  onClear,
  width = "8rem",
  isSm = false
}) => {
  const [showModal, setShowModal] = useState(false);

  const hasValue = selectedAmenities.length > 0;

  const handleClear = e => {
    e.stopPropagation();
    onClear();
  };

  const handleToggleAmenity = amenityValue => {
    const isSelected = selectedAmenities.includes(amenityValue);
    if (isSelected) {
      onSelectionChange(selectedAmenities.filter(a => a !== amenityValue));
    } else {
      onSelectionChange([...selectedAmenities, amenityValue]);
    }
  };

  const formatDisplayValue = () => {
    if (selectedAmenities.length === 0) {
      return label;
    } else if (selectedAmenities.length === 1) {
      return `${selectedAmenities.length} Feature${
        selectedAmenities.length > 1 ? "s" : ""
      }`;
    }
  };

  // Group amenities by their group field
  const groupedAmenities = amenityOptions.reduce((acc, amenity) => {
    const group = amenity.group || "other";
    if (!acc[group]) {
      acc[group] = [];
    }
    acc[group].push(amenity);
    return acc;
  }, {});

  // Sort amenities within each group by order
  Object.keys(groupedAmenities).forEach(group => {
    groupedAmenities[group].sort((a, b) => a.order - b.order);
  });

  // Get group names for display (convert kebab-case to Title Case)
  const formatGroupName = groupKey => {
    // customizable based on groupKey value
    switch (groupKey) {
      case "accessibility":
        return "Accessibility";
      case "backofhouse":
        return "Back of House";
      case "climate":
        return "Climate Control";
      case "frontage":
        return "Frontage";
      case "kitchen":
        return "Kitchen";
      case "parking":
        return "Parking";
      case "plumbing-restrooms":
        return "Plumbing & Restrooms";
      case "power-data":
        return "Power & Data";
      case "safety":
        return "Safety";
      case "shared-amenities":
        return "Shared Amenities";
      default:
        return "Other";
    }
  };

  const groupNames = Object.keys(groupedAmenities).sort();

  return (
    <>
      <button
        className="btn btn-sm btn-secondary with-icon"
        onClick={() => setShowModal(true)}
        style={{
          width: width,
          marginRight: "0.5rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap"
        }}
      >
        {formatDisplayValue()}

        {hasValue && (
          <>
            <div className="flex-grow" />
            <div
              style={{
                float: "right",
                cursor: "pointer",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
              onClick={handleClear}
            >
              <i className="fa-solid fa-xmark"></i>
            </div>
          </>
        )}
      </button>

      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        centered
        fullscreen={isSm}
        size="xl"
        scrollable={true}
      >
        <Modal.Header closeButton>
          <Modal.Title>Select Features</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: "2rem" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isSm ? "1fr" : "repeat(3, 1fr)",
              gap: "2rem"
            }}
          >
            {groupNames.map(groupKey => (
              <div key={groupKey} style={{ minWidth: "0" }}>
                <div
                  style={{
                    fontWeight: "600",
                    fontSize: "1rem",
                    color: "#495057",
                    marginBottom: "1rem",
                    paddingBottom: "0.5rem",
                    borderBottom: "2px solid #dee2e6"
                  }}
                >
                  {formatGroupName(groupKey)}
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.5rem"
                  }}
                >
                  {groupedAmenities[groupKey].map(amenity => {
                    const isSelected = selectedAmenities.includes(
                      amenity.value
                    );
                    return (
                      <label
                        key={amenity.value}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          cursor: "pointer",
                          padding: "0.5rem",
                          borderRadius: "4px",
                          fontSize: "0.9rem",
                          transition: "background-color 0.15s"
                        }}
                        onMouseEnter={e =>
                          (e.currentTarget.style.backgroundColor = "#f8f9fa")
                        }
                        onMouseLeave={e =>
                          (e.currentTarget.style.backgroundColor =
                            "transparent")
                        }
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggleAmenity(amenity.value)}
                          style={{ marginRight: "0.75rem", cursor: "pointer" }}
                        />
                        <i
                          className={`fa-solid ${amenity.icon}`}
                          style={{
                            marginRight: "0.75rem",
                            width: "1.25rem",
                            textAlign: "center",
                            color: "#6c757d",
                            fontSize: "0.9rem"
                          }}
                        />
                        <span>{amenity.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => {
              onClear();
              setShowModal(false);
            }}
          >
            Clear All
          </Button>
          <Button variant="primary" onClick={() => setShowModal(false)}>
            Apply
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

// allowAnySaleOrLease: the business locator's "Available spaces" layer, which starts on sale and lease listings
// together; the RE locator starts on lease and can also show listings under development.
const RELocatorFilters = ({
  className,
  onFilterChange,
  searchResults,
  allowAnySaleOrLease = false
}) => {
  const isSm = useMediaQuery({ query: "(max-width: 767.98px)" });

  const { pauAmenities } = useAppData();
  const amenityOptions = pauAmenities || [];

  const categoryOptions = [
    {
      label: "Residential",
      value: "RESIDENTIAL",
      icon: "fa-house",
      searchType: "CATEGORY",
      searchTerms: ["residential"]
    },
    {
      label: "Office",
      value: "OFFICE",
      icon: "fa-chair-office",
      searchType: "CATEGORY",
      searchTerms: ["office"]
    },
    {
      label: "Industrial",
      value: "INDUSTRIAL",
      icon: "fa-industry",
      searchType: "CATEGORY",
      searchTerms: ["industrial"]
    },
    {
      label: "Retail",
      value: "RETAIL",
      icon: "fa-bag-shopping",
      searchType: "CATEGORY",
      searchTerms: ["retail"]
    },
    {
      label: "Medical",
      value: "MEDICAL",
      icon: "fa-kit-medical",
      searchType: "CATEGORY",
      searchTerms: ["medical"]
    },
    {
      label: "Food",
      value: "FOOD",
      icon: "fa-fork-knife",
      searchType: "CATEGORY",
      searchTerms: ["food"]
    },
    {
      label: "Event",
      value: "EVENT",
      icon: "fa-party-bell",
      searchType: "CATEGORY",
      searchTerms: ["event"]
    },
    {
      label: "Storage",
      value: "STORAGE",
      icon: "fa-warehouse",
      searchType: "CATEGORY",
      searchTerms: ["storage"]
    },
    {
      label: "Other",
      value: "OTHER",
      icon: "fa-kit fa-solid-building-circle-question",
      searchType: "CATEGORY",
      searchTerms: ["other"]
    }
  ];

  const saleOrLeaseOptions = allowAnySaleOrLease
    ? [
        { value: "ALL", label: "Sale or Lease" },
        { value: "LEASE", label: "For Lease" },
        { value: "SALE", label: "For Sale" }
      ]
    : [
        { value: "LEASE", label: "For Lease" },
        { value: "SALE", label: "For Sale" },
        { value: "UNDER_DEVELOPMENT", label: "Under Development" }
      ];

  const [saleOrLease, setSaleOrLease] = useState(
    allowAnySaleOrLease ? null : "LEASE"
  );
  const hasPrice = saleOrLease === "SALE" || saleOrLease === "LEASE";
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedAmenities, setSelectedAmenities] = useState([]);
  const [minAreaSqFt, setMinAreaSqFt] = useState(null);
  const [maxAreaSqFt, setMaxAreaSqFt] = useState(null);
  const [minPrice, setMinPrice] = useState(null);
  const [maxPrice, setMaxPrice] = useState(null);

  useEffect(() => {
    onFilterChange({
      saleOrLease: saleOrLease,
      category: selectedCategory?.value ?? null,
      selectedAmenities: selectedAmenities ?? null,
      minAreaSqFt: minAreaSqFt ?? null,
      maxAreaSqFt: maxAreaSqFt ?? null,
      minPrice: minPrice ?? null,
      maxPrice: maxPrice ?? null
    });
  }, [
    saleOrLease,
    selectedCategory,
    selectedAmenities,
    minAreaSqFt,
    maxAreaSqFt,
    minPrice,
    maxPrice
  ]);

  return (
    <div className="search-filters">
      <div className="default-flex-row-layout">
        <Dropdown
          onSelect={option => {
            setSaleOrLease(option === "ALL" ? null : option);
          }}
          style={{ width: "8rem" }}
        >
          <Dropdown.Toggle
            variant="secondary"
            className="btn btn-sm btn-secondary"
            style={{ width: "8rem", marginRight: "0.5rem" }}
          >
            {
              saleOrLeaseOptions.find(
                option => option.value === (saleOrLease ?? "ALL")
              )?.label
            }
          </Dropdown.Toggle>
          <Dropdown.Menu
            renderOnMount={true}
            popperConfig={{ strategy: "fixed" }}
          >
            {saleOrLeaseOptions.map(option => (
              <Dropdown.Item key={option.value} eventKey={option.value}>
                {option.label}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
        <Dropdown
          onSelect={category => {
            const selectedCategory = categoryOptions.find(
              c => c.value === category
            );
            setSelectedCategory(selectedCategory);
          }}
          style={{ width: "8rem" }}
        >
          <Dropdown.Toggle
            variant="secondary"
            className="btn btn-sm btn-secondary"
            style={{ width: "8rem", marginRight: "0.5rem" }}
          >
            {selectedCategory?.label ?? "Space Type"}
          </Dropdown.Toggle>
          <Dropdown.Menu
            //   className="multi-column-dropdown"
            renderOnMount={true}
            popperConfig={{ strategy: "fixed" }}
          >
            {categoryOptions?.map(category => (
              <Dropdown.Item className="with-icon" eventKey={category.value}>
                <i className={`fa-solid ${category.icon}`}></i>
                {category.label}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
        {hasPrice && (
          <RangeDropdown
            label={saleOrLease === "SALE" ? "Price" : "Rent"}
            minValue={minPrice}
            maxValue={maxPrice}
            onMinChange={setMinPrice}
            onMaxChange={setMaxPrice}
            onClear={() => {
              setMinPrice(null);
              setMaxPrice(null);
            }}
            minPlaceholder="Min $"
            maxPlaceholder="Max $"
            prefix="$"
          />
        )}
        <RangeDropdown
          label="Size"
          minValue={minAreaSqFt}
          maxValue={maxAreaSqFt}
          onMinChange={setMinAreaSqFt}
          onMaxChange={setMaxAreaSqFt}
          onClear={() => {
            setMinAreaSqFt(null);
            setMaxAreaSqFt(null);
          }}
          minPlaceholder="Min SF"
          maxPlaceholder="Max SF"
        />
        <AmenityDropdown
          label="Features"
          amenityOptions={amenityOptions}
          selectedAmenities={selectedAmenities}
          onSelectionChange={setSelectedAmenities}
          onClear={() => setSelectedAmenities([])}
          isSm={isSm}
        />
      </div>
      {searchResults != null && searchResults.length > 0 ? (
        <div className="default-flex-row-layout"></div>
      ) : (
        <div className="default-flex-row-layout"></div>
      )}
    </div>
  );
};

export default RELocatorFilters;
